import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, DollarSign, Megaphone, Users, ShieldCheck, FileText, Presentation, CloudUpload, Loader2, X, Mail, Link as LinkIcon, Landmark, ChevronDown, Building2 } from "lucide-react";
import TenderTab from "./business/TenderTab";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const money = (v) => `$${Number(v).toLocaleString()}`;
const card = "border border-gold-500/25 bg-navy-900/40 p-6";
const th = "py-2.5 px-3 font-mono uppercase tracking-widest text-[0.55rem] text-white/40 text-left";
const td = "py-2.5 px-3 text-white/75 font-light";

const TIERS = [
  { tier: 1, name: "Small Rural", revenue: "Under $60M", profile: "Remote / outback councils, small shires, island councils", perEvent: 8550, annual: 17100, saving: "33%", net75: 2139, net50: 4275 },
  { tier: 2, name: "Regional", revenue: "$60M – $240M", profile: "Mid-size regional councils, coastal shires", perEvent: 13500, annual: 28800, saving: "29%", net75: 3375, net50: 6750 },
  { tier: 3, name: "Large Regional / Urban", revenue: "$240M – $750M", profile: "Large regional cities, outer-metro councils", perEvent: 21600, annual: 49500, saving: "24%", net75: 5400, net50: 10800 },
  { tier: 4, name: "Major Council", revenue: "Over $750M", profile: "Metropolitan fringe, large coastal cities", perEvent: 34200, annual: 76500, saving: "25%", net75: 8550, net50: 17100 },
];

const ADDONS = [
  ["Unlimited users", "+$2,700 / event", "+$5,400 / yr", "Removes the 10-user cap for large teams"],
  ["Priority support", "+$1,800 / event", "+$3,600 / yr", "4-hour response SLA, phone support"],
  ["Onboarding training (remote, 2hr)", "$4,500 one-off", "—", "Live walkthrough for the council DRFA team"],
  ["Onboarding training (on-site, full day)", "$22,500 one-off", "—", "On-site training at council premises"],
  ["Custom logo & branding", "$2,700 one-off", "—", "Council logo on PDF exports and watermarks"],
  ["Multi-council consortium", "Contact us", "—", "Volume pricing for regional organisations of councils"],
];

const CONSORTIUM = [["5–9 councils", "15%"], ["10–19 councils", "20%"], ["20+ councils", "25%"], ["State-wide LGA agreement", "Negotiated"]];

const MODULES = [
  ["FieldClaim Core", "Field evidence capture, claims, QA engine, all state export packs, DRFA program tracker", 7750, 12900, 22200, 34300],
  ["EW Prep", "AI damage-photo scanner on the RDD-2022 world-standard taxonomy, regionally verified costing, DTMR Form 1 & NSWRA EW schedule compilers", 3550, 5900, 10150, 15700],
  ["Contract Admin", "AS 2124 / AS 4000 administration — progress claims, SOPA clocks, variations, EOT time-bars, retention", 2700, 4500, 7750, 11950],
  ["Assessor Mode", "Assessment workspace — ledger finalisation, on-costs, factsheet rules, lodgement readiness", 2350, 3900, 6700, 10350],
  ["RAAgent", "DRFA submission builder & assessor-grade QA/evaluation engine", 2150, 3600, 6200, 9600],
  ["Portfolio & Reporting", "Program budgets, transparent health status, risks, AI executive reports", 2150, 3600, 6200, 9600],
  ["AI Expert & Library", "Grounded AI answers from the council's own document library", 1750, 2900, 5000, 7700],
  ["TimeCapture", "Crew hours self-recording, allocation to assets as claimable actuals", 1450, 2400, 4150, 6400],
];
const MODULE_TOTALS = [23850, 39700, 68350, 105600];

function BundleCalculator() {
  const [tier, setTier] = useState(1);
  const [sel, setSel] = useState(["FieldClaim Core", "EW Prep"]);
  const toggle = (name) => setSel((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));
  const sum = MODULES.filter((m) => sel.includes(m[0])).reduce((a, m) => a + m[2 + tier], 0);
  const suite = TIERS[tier].annual;
  const suiteWins = sum >= suite;
  return (
    <div className="mt-6 border border-gold-500/25 bg-navy-950/50 p-5" data-testid="bh-bundle-calc">
      <Mono className="text-gold-400 text-[0.55rem]">Build your bundle</Mono>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {TIERS.map((t, i) => (
          <button key={t.tier} onClick={() => setTier(i)} data-testid={`bh-calc-tier-${t.tier}`}
            className={`px-3 py-1.5 text-[0.65rem] font-mono uppercase tracking-widest border transition-colors ${tier === i ? "bg-gold-500 text-navy-950 border-gold-500 font-semibold" : "border-gold-500/25 text-white/55 hover:text-gold-400"}`}>
            T{t.tier} {t.name}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {MODULES.map(([name, , ...prices]) => {
          const on = sel.includes(name);
          return (
            <button key={name} onClick={() => toggle(name)} data-testid={`bh-calc-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-3 py-2 text-xs border transition-colors ${on ? "border-gold-500 bg-gold-500/10 text-gold-300" : "border-gold-500/20 text-white/50 hover:text-white"}`}>
              {name} · {money(prices[tier])}
            </button>
          );
        })}
      </div>
      <div className="grid md:grid-cols-3 gap-3 mt-5">
        <div className="border border-gold-500/20 px-4 py-3">
          <Mono className="text-[0.45rem] text-white/40 block">Your bundle ({sel.length} module{sel.length === 1 ? "" : "s"})</Mono>
          <div className="font-mono text-xl text-white mt-1" data-testid="bh-calc-bundle-total">{money(sum)}<span className="text-xs text-white/40">/yr</span></div>
        </div>
        <div className="border border-gold-500/20 px-4 py-3">
          <Mono className="text-[0.45rem] text-white/40 block">Full suite (everything)</Mono>
          <div className="font-mono text-xl text-emerald-300 mt-1">{money(suite)}<span className="text-xs text-white/40">/yr</span></div>
        </div>
        <div className={`border px-4 py-3 ${suiteWins ? "border-emerald-400/40" : "border-gold-500/20"}`} data-testid="bh-calc-verdict">
          <Mono className="text-[0.45rem] text-white/40 block">Recommendation</Mono>
          <div className={`text-sm mt-1.5 ${suiteWins ? "text-emerald-300" : "text-white/70"}`}>
            {sel.length === 0 ? "Pick at least one module" : suiteWins ? `Take the suite — save ${money(sum - suite)}/yr and get every module` : `Bundle is ${money(suite - sum)} cheaper — suite pays off at 3+ modules`}
          </div>
        </div>
      </div>
    </div>
  );
}

const WASA_PLAN = [
  {
    code: "WA", door: "DFES is the door. One small, centralised team administers DRFAWA — if DFES recognises the FieldClaim export format, every shire follows. Sell the regulator first, then the councils.",
    phases: [
      ["Phase 1 · Now — intel & regulator contact", "Call DFES Recovery Funding (1800 920 659 / drfawa@dfes.wa.gov.au): confirm portal claim format + evidence expectations against our WA DFES export. Map every DRFAWA activation from the past 24 months. Shortlist 2–3 target shires in the Kimberley / Pilbara / Gascoyne cyclone-flood belt."],
      ["Phase 2 · 30–60 days — procurement & pilot", "WALGA Preferred Supplier Program application (the WA council procurement pathway). VendorPanel listing flagged for WA. Offer one flood-affected shire a free event alpha mirroring the Bundaberg/Clarence model — remote shires with tiny works teams are the strongest proof case."],
      ["Phase 3 · 60–90+ days — activation playbook", "24-hour post-activation outreach trigger (same as QLD). Demo the DFES-format CSV export live. WA LG Convention presence. Publish the alpha case study with before/after claim-compilation hours."],
    ],
  },
  {
    code: "SA", door: "The state controls the audit gate — DPC discretion, a state-appointed auditor, the lowest recovery rates in the nation. Don't sell council-by-council: win the state's confidence in the evidence format and every council claim benefits. Top-down, not door-to-door.",
    phases: [
      ["Phase 1 · Now — state & sector contact", "DPC State Administering Agency introduction: put the audited claim pack (direct-connection manifest) in front of the people who brief the state-appointed auditor. Engage LGA SA — they publicly lobby that councils are over-exposed; FieldClaim is the sector answer they can point to. Shortlist River Murray 2022-23 flood councils."],
      ["Phase 2 · 30–60 days — procurement & proof", "LG Procurement (LGA SA) panel listing. 'Audit-proof by design' whitepaper to council CEOs — lead with the Flinders Ranges precedent (small councils forced onto consultants just to lodge). Target the Legatus Group (largest SA regional LGA grouping) for a multi-council conversation."],
      ["Phase 3 · 60–90+ days — the state-wide play", "Push for a negotiated state-wide LGA SA agreement (consortium framework). Run an annual 'claim season' campaign Oct–Feb, ahead of the state's 31 March Commonwealth submission — the one moment every SA council feels the audit pressure at once."],
    ],
  },
];

const STATES = [
  {
    code: "NSW", lead: "Lead with cost recovery", message: "\u201cYour DRFA claim can pay for the app.\u201d",
    detail: "Highest-priority market — the licence fee is conditionally eligible for DRFA reimbursement under NSW RA guidance (EPAR \u2018computers, software licences and office equipment rental\u2019). Net cost for a Tier 2 council at 75% cost-sharing: $3,375 per event.",
    channels: ["NSW RA webinars & workshops (demo slot)", "LGNSW Annual Conference — Oct 2026", "LGNSW Procurement Panel listing", "Direct outreach to flood-affected councils (Central West 2022, 25+ councils)", "LinkedIn job-title targeting (Works Manager, DRFA Coordinator)"],
    personas: [["DRFA Coordinator", "Stop losing claims over photo evidence — the app the guidelines were written for."], ["Finance Manager", "$3,375 net cost after DRFA reimbursement — a fraction of a day of consultant fees."], ["CEO / GM", "Every photo GPS-tagged, timestamped and compliant. Claims are defensible."]],
  },
  {
    code: "QLD", lead: "Lead with claim success rate", message: "\u201cDon\u2019t let inadequate photos cost you your claim.\u201d",
    detail: "QRA rejects ~15–20% of initial DRFA submissions for insufficient documentation. A single rejected $1.5M road restoration = a $1.125M loss at 75% cost-sharing. The app is the insurance premium against that risk.",
    channels: ["LGAQ Annual Conference — Oct 2026", "QAssure ICT Marketplace listing", "QRA Regional Liaison Officer briefings", "Post-TC Alfred outreach (SE QLD, free 30-day trial)", "IPWEAQ conference + journal"],
    personas: [["DRFA Coordinator", "QRA Tip Sheet 3 compliance, built in — GPS-tagged, directionally referenced, folder-organised."], ["Finance Manager", "A $13,500 licence that protects a $1,500,000 claim."], ["CEO / GM", "Documentation meets QRA's published standards. Every time."]],
  },
  {
    code: "VIC", lead: "Lead with CMS compliance & VAGO audit risk", message: "\u201cVictoria\u2019s new CMS portal demands better evidence. Be ready.\u201d",
    detail: "The EMV Claims Management System (July 2023) raised the documentation burden, and VAGO's 2025 audit flagged council DRFA documentation deficiencies. The 31 Aug 2026 CMS lodgement deadline for the Jan 2026 bushfires creates urgency.",
    channels: ["MAV Annual Conference — May 2026 (primary launch)", "LGPro Annual Conference — Aug 2026", "Direct outreach to 2026 bushfire-affected councils (~15)", "EMV REMI network briefings", "VAGO audit findings as CEO/GM hook"],
    personas: [["DRFA Coordinator", "Built for the CMS portal — photos, GPS and cost items export-ready in minutes."], ["Finance Manager", "The VAGO audit found documentation gaps. This closes them."], ["CEO / GM", "Your DRFA claims are VAGO-audit-ready."]],
  },
  {
    code: "WA", lead: "Lead with deadline + evidence risk", message: "\u201cSix months to apply, one shot at the portal. Capture it right the first time.\u201d",
    detail: "DFES administers DRFAWA via the Recovery Funding Submissions & Enquiry Portal: PEN form within 6 months of declaration, reimbursement claims by 30 Sept after FY end. DFES already demands GPS-tagged before/after imagery, IDAs and verified invoices \u2014 missing evidence means rejection or clawback. Vast LGA road networks (shires like East Pilbara manage 1000s of km with tiny works teams) make manual evidence collection the #1 failure point. FieldClaim exports a portal-mapped CSV.",
    channels: ["DFES Recovery Funding intro (1800 920 659 / drfawa@dfes.wa.gov.au)", "WALGA Preferred Supplier Program listing", "Cyclone/flood-prone target shires (Kimberley, Pilbara, Gascoyne)", "WA LG Convention", "Post-event outreach within 24h of a DRFAWA activation"],
    personas: [["Works Manager", "PEN-to-claim evidence captured in the field \u2014 GPS-tagged, before/after, portal-ready."], ["Finance Manager", "Claims lodged by 30 September without a consultant re-build of missing records."], ["CEO / Shire President", "No clawbacks. Every dollar claimed can be evidenced years later."]],
  },
  {
    code: "SA", lead: "Lead with audit-proof by design", message: "\u201cThe state-appointed auditor signs your claim. Give them nothing to reject.\u201d",
    detail: "SA councils are the most exposed in the country: highest reimbursement thresholds, lowest recovery rates, and every annual claim must survive a state-appointed audit (DPC amalgamates and submits to the Commonwealth by 31 March). Common pain: onerous direct-connection evidence rules, community infrastructure excluded, tree/verge damage ill-defined, and small regional councils (e.g. Flinders Ranges post-Quorn floods) forced to hire consultants just to lodge. FieldClaim's audited claim pack ships the direct-connection manifest the auditor asks for.",
    channels: ["DPC State Administering Agency intro", "LGA Procurement (LGA SA) panel listing", "River Murray flood-affected councils as first targets", "LGA SA conference + Regional Organisations", "\u2018Audit-proof by design\u2019 whitepaper to council CEOs"],
    personas: [["DRFA Coordinator", "Direct connection \u2014 event to expenditure \u2014 evidenced automatically for every asset."], ["Finance Manager", "The audited annual claim schedule builds itself from field capture."], ["CEO / GM", "Lowest recovery rates in the nation \u2014 unless your evidence is bulletproof. Ours is."]],
  },
];

const MILESTONES = [
  ["App publicly available", "June 2026"], ["TC Alfred outreach campaign", "June 2026"],
  ["QAssure / LGNSW panel applications", "June–July 2026"], ["NSW RA webinar inclusion", "July 2026"],
  ["LGAQ Annual Conference", "October 2026"], ["LGNSW Annual Conference", "October 2026"],
  ["IPWEAQ Conference + journal article", "November 2026"], ["First ROC consortium agreement", "December 2026"],
  ["50-council milestone", "June 2027"],
];

const REVENUE = [
  ["Year 1 (FY2026-27)", "12 councils × 1.5 events avg", "$16,200", "$972,000"],
  ["Year 2 (FY2027-28)", "28 councils × 1.8 events avg", "$19,800", "$2,217,600"],
  ["Year 3 (FY2028-29)", "55 councils × 2.0 events avg", "$23,400", "$5,148,000"],
];

const STAKEHOLDER_CATS = [
  { name: "Field Operators", who: "Works inspectors, disaster coordinators, field consultants", response: "Large touch targets, real-time GPS lock, burned-in watermarks, reference photo navigator, live compass, offline-first PWA." },
  { name: "Council Administration", who: "DRFA Coordinator, Finance / CFO, CEO / GM, IT Manager", response: "Three-folder evidence hierarchy, pre-submission QA engine, status workflow, category-coded cost estimator, data-sovereignty-by-design (device-first storage, no third-party tracking)." },
  { name: "State Funding Agencies", who: "QRA RLOs, NSW RA Program Delivery, EMV REMIs", response: "MARS ZIP with QRA filename convention, SmartyGrants CSV/PDF + Schedule of Quantities, VIC CMS data CSV, GL-3 compliant photo structure." },
  { name: "Commonwealth Government", who: "NEMA, Commonwealth Treasury", response: "Independently verifiable GPS + timestamp evidence chain, full JSON backup for audit, itemised unit-rate cost basis mapped to DRFA-eligible categories." },
  { name: "Community & Asset Users", who: "Residents, businesses, road users, emergency services", response: "Claim preparation reduced from 40–80 hours to under 10 — faster funding, faster restoration; shareable accurate location data." },
  { name: "Commercial Partners", who: "Civil contractors, claim-management consultants, insurance assessors", response: "Complete staged photo record, ZIP hand-over packages, per-site backup/restore for multi-client work, co-branding add-on." },
];

const COMMITMENTS = [
  "Annual review of QRA, NSW RA and EMV published guidelines for continued compliance.",
  "Presence at LGAQ, LGNSW and MAV annual conferences for direct coordinator feedback.",
  "Ongoing engagement with QRA RLOs, NSW RA Program Delivery Officers and EMV REMIs.",
  "Annual stakeholder impact report — claims supported, funding value facilitated, success rates.",
];

const TABS = [
  { key: "pricing", label: "Pricing Framework", icon: DollarSign },
  { key: "marketing", label: "Marketing Plan", icon: Megaphone },
  { key: "stakeholders", label: "Stakeholder Statement", icon: Users },
  { key: "tender", label: "Tender Prep", icon: Landmark },
];

const GROUPS = [
  { id: "council", label: "Council cold call / email", icon: Building2, items: [
    { kind: "gmail", value: "council_nsw", label: "One-click NSW intro — Gmail draft + docs", tag: "gmail" },
    { kind: "gmail", value: "council_qld", label: "One-click QLD intro — Gmail draft + docs", tag: "gmail" },
    { kind: "doc", value: "pitchkit", label: "State pitch playbook — call scripts & emails", tag: "doc" },
    { kind: "href", value: "/EW_Prep_Pitch_60s.mp4", label: "EW Prep pitch cut — send this first (54s)", tag: "video" },
    { kind: "href", value: "/MDAu_Office_Tour.mp4", label: "Full platform tour (1m46)", tag: "video" },
    { kind: "doc", value: "brochure", label: "Glossy brochure (print / PDF)", tag: "doc" },
    { kind: "doc", value: "pricing", label: "Pricing framework", tag: "doc" },
  ]},
  { id: "stategov", label: "State gov cold call / email", icon: Landmark, items: [
    { kind: "href", value: "/WI-OR-01_Outreach_JJ99Q2y2i8Y.pdf", label: "Outreach call scripts — DFES / DPC / LGA (WI-OR-01)", tag: "pdf" },
    { kind: "doc", value: "capability", label: "Capability statement", tag: "doc" },
    { kind: "doc", value: "dossier", label: "Platform dossier", tag: "doc" },
    { kind: "href", value: "/MDAu_Office_Tour.mp4", label: "Full platform tour (1m46)", tag: "video" },
  ]},
  { id: "meeting", label: "Meeting & demo", icon: Presentation, items: [
    { kind: "demo", label: "Assessor demo link (no login)", tag: "live" },
    { kind: "href", value: "/MDAu_EW_Prep_Walkthrough.mp4", label: "EW Prep walkthrough (2m20)", tag: "video" },
    { kind: "href", value: "/FieldClaim_Product_Tour.mp4", label: "FieldClaim tour (2m04)", tag: "video" },
    { kind: "href", value: "/MDAu_Assessor_Mode_Walkthrough.mp4", label: "Assessor mode (3m05)", tag: "video" },
    { kind: "href", value: "/MDAu_RAAgent_Walkthrough.mp4", label: "RAAgent (56s)", tag: "video" },
    { kind: "href", value: "/MDAu_Portfolio_Walkthrough.mp4", label: "Portfolio (51s)", tag: "video" },
    { kind: "doc", value: "walkthrough", label: "Product walkthrough (doc)", tag: "doc" },
  ]},
  { id: "followup", label: "Leave-behind / follow-up", icon: FileText, items: [
    { kind: "doc", value: "dossier", label: "Platform dossier — the master leave-behind", tag: "doc" },
    { kind: "doc", value: "capability", label: "Capability statement", tag: "doc" },
    { kind: "doc", value: "clarence", label: "Clarence Valley proposal", tag: "doc" },
    { kind: "doc", value: "bundaberg", label: "Bundaberg proposal", tag: "doc" },
    { kind: "doc", value: "alpha", label: "Alpha program", tag: "doc" },
  ]},
  { id: "internal", label: "Internal", icon: Lock, items: [
    { kind: "doc", value: "appstore", label: "App Store submission runbook", tag: "doc" },
    { kind: "doc", value: "marketing", label: "Marketing plan", tag: "doc" },
    { kind: "doc", value: "assessor", label: "Assessor mode paper", tag: "doc" },
    { kind: "doc", value: "architecture", label: "Build & architecture", tag: "doc" },
    { kind: "doc", value: "confidence", label: "Confidentiality deed", tag: "doc" },
  ]},
];

export default function BusinessHub() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pricing");
  const [demoMsg, setDemoMsg] = useState(null);
  const [demoStats, setDemoStats] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);

  const loadDemoStats = () => api.get("/assessor-demo/stats").then((r) => setDemoStats(r.data)).catch(() => {});
  useEffect(() => { if (["admin", "staff"].includes(user?.role)) loadDemoStats(); }, [user?.role]);

  const openDoc = (doc) => {
    const t = localStorage.getItem("mdau_token");
    window.open(`${API}/business/${doc}?token=${t}`, "_blank");
  };

  const gmailDraft = async (template) => {
    toast.loading("Rendering docs and creating the Gmail draft…", { id: "bh-gmail" });
    try {
      const { data } = await api.post("/drive/draft-email", { template });
      toast.success(`Draft created in Gmail — "${data.subject}" with ${data.attachments.length} PDF attachment(s). Add the recipient and send.`, { id: "bh-gmail", duration: 8000 });
      window.open("https://mail.google.com/mail/u/0/#drafts", "_blank");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Draft creation failed.", { id: "bh-gmail" });
    }
  };

  const demoLink = async () => {
    setDemoMsg({ busy: true });
    try {
      const { data } = await api.post("/assessor-demo/setup");
      const url = `${window.location.origin}${data.path}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      setDemoMsg({ ok: true, url, views: data.views, last: data.last_viewed_at });
      loadDemoStats();
    } catch (e) {
      setDemoMsg({ ok: false, text: formatApiErrorDetail(e?.response?.data?.detail) || "Could not create the demo link." });
    }
  };

  if (!["admin", "staff"].includes(user?.role)) {
    return (
      <div className="text-white max-w-lg mx-auto text-center py-24" data-testid="business-restricted">
        <Lock className="h-10 w-10 text-gold-400 mx-auto mb-5" />
        <h1 className="font-heading font-light text-3xl">Restricted</h1>
        <p className="text-white/50 font-light mt-3">Business materials are visible to administrators only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 text-white" data-testid="business-hub-page">
      <div>
        <Mono className="text-gold-400 text-[0.65rem] inline-flex items-center gap-2"><Lock className="h-3 w-3" /> Confidential · internal only</Mono>
        <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Business Hub</h1>
        <p className="text-white/55 mt-3 font-light max-w-2xl">
          Commercial strategy for FieldClaim by CBKM.au — per-event pricing framework, state-by-state marketing plan and the
          stakeholder consideration statement. Locked behind administrator access.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`bh-tab-${t.key}`}
            className={`inline-flex items-center gap-2 border px-4 py-2.5 text-sm transition-colors ${tab === t.key ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/55 hover:text-gold-300"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
        <span className="mx-1 h-6 w-px bg-gold-500/20 hidden sm:block" />
        {GROUPS.map((g) => (
          <div key={g.id} className="relative">
            <button onClick={() => setOpenMenu(openMenu === g.id ? null : g.id)} data-testid={`bh-group-${g.id}`}
              className={`inline-flex items-center gap-2 border px-4 py-2.5 text-sm transition-colors ${openMenu === g.id ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/40 text-gold-300 hover:bg-gold-500/10"}`}>
              <g.icon className="h-4 w-4" /> {g.label} <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {openMenu === g.id && (
              <div className="absolute z-40 mt-1 w-80 bg-navy-900 border border-gold-500/30 shadow-xl" data-testid={`bh-group-${g.id}-menu`}>
                {g.items.map((it) => {
                  const cls = "w-full flex items-center justify-between gap-3 text-left px-4 py-2.5 text-sm text-white/75 hover:bg-gold-500/10 hover:text-gold-300 border-b border-gold-500/10 last:border-0";
                  const tag = <span className="font-mono text-[0.5rem] uppercase tracking-widest text-white/30 shrink-0">{it.tag}</span>;
                  if (it.kind === "href") return (
                    <a key={it.label} href={it.value} target="_blank" rel="noreferrer" onClick={() => setOpenMenu(null)} className={cls}
                      data-testid={`bh-item-${it.value.slice(1, 16)}`}>{it.label}{tag}</a>
                  );
                  if (it.kind === "demo") return (
                    <button key={it.label} onClick={() => { setOpenMenu(null); demoLink(); }} disabled={demoMsg?.busy} className={cls} data-testid="bh-assessor-demo">
                      <span className="inline-flex items-center gap-2">{demoMsg?.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LinkIcon className="h-3.5 w-3.5" />} {it.label}
                        {demoStats?.exists ? <span className="font-mono text-[0.55rem] border border-sky-400/40 text-sky-300 px-1.5 py-0.5 rounded-sm" data-testid="bh-demo-views">{demoStats.views} view{demoStats.views === 1 ? "" : "s"}</span> : null}</span>{tag}
                    </button>
                  );
                  if (it.kind === "gmail") return (
                    <button key={it.label} onClick={() => { setOpenMenu(null); gmailDraft(it.value); }} className={cls} data-testid={`bh-gmail-${it.value}`}>
                      <span className="inline-flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gold-400" /> {it.label}</span>{tag}
                    </button>
                  );
                  return (
                    <button key={it.label} onClick={() => { openDoc(it.value); setOpenMenu(null); }} className={cls} data-testid={`bh-doc-${it.value}`}>{it.label}{tag}</button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      {demoMsg && !demoMsg.busy && (
        <p className={`text-xs font-light ${demoMsg.ok ? "text-emerald-300" : "text-amber-300"}`} data-testid="bh-demo-msg">
          {demoMsg.ok ? <>Public read-only demo link copied to clipboard — share it with anyone (no login needed): <a href={demoMsg.url} target="_blank" rel="noreferrer" className="underline text-sky-300 break-all" data-testid="bh-demo-url">{demoMsg.url}</a>
            <span className="text-white/45 block mt-1">Viewed {demoMsg.views || 0} time{demoMsg.views === 1 ? "" : "s"}{demoMsg.last ? ` · last opened ${demoMsg.last.slice(0, 16).replace("T", " ")} UTC` : " — you'll see it tick up here when Andrew opens it"}.</span></> : demoMsg.text}
        </p>
      )}

      <DriveCard />

      {tab === "pricing" && <PricingTab />}
      {tab === "marketing" && <MarketingTab />}
      {tab === "stakeholders" && <StakeholderTab />}
      {tab === "tender" && <TenderTab />}
    </div>
  );
}

function DriveCard() {
  const [status, setStatus] = useState(null);
  const [busyDoc, setBusyDoc] = useState(null);
  const [msg, setMsg] = useState(null);
  const docs = [["brochure", "Brochure"], ["walkthrough", "Walkthrough"], ["pricing", "Pricing"], ["marketing", "Marketing"], ["confidence", "Confidentiality deed"], ["alpha", "Alpha program"], ["architecture", "Build & architecture"], ["capability", "Capability statement"], ["assessor", "Assessor mode"], ["clarence", "Clarence Valley proposal"], ["bundaberg", "Bundaberg proposal"], ["pitchkit", "State pitch playbook"]];

  const load = () => api.get("/drive/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false }));
  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive") === "connected") {
      setMsg({ ok: true, text: "Google Drive connected." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("drive") === "error") {
      setMsg({ ok: false, text: `Google connection failed: ${params.get("reason") || "unknown error"}` });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async () => {
    try { const { data } = await api.get("/drive/connect"); window.location.href = data.authorization_url; }
    catch (e) { setMsg({ ok: false, text: formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't start Drive connection." }); }
  };
  const disconnect = async () => { await api.post("/drive/disconnect"); setMsg(null); load(); };
  const push = async (doc) => {
    setBusyDoc(doc); setMsg(null);
    try { const { data } = await api.post("/drive/push", { kind: "business", doc_type: doc }); setMsg({ ok: true, text: `Saved ${data.file_name} → Drive / ${data.folder}`, link: data.web_link }); }
    catch (e) { setMsg({ ok: false, text: formatApiErrorDetail(e?.response?.data?.detail) || "Drive save failed." }); }
    finally { setBusyDoc(null); }
  };
  const draft = async (template) => {
    setBusyDoc(`draft-${template}`); setMsg(null);
    try { const { data } = await api.post("/drive/draft-email", { template }); setMsg({ ok: true, text: `Draft created in your Gmail — "${data.subject}" with ${data.attachments.length} PDF attachment(s). Review and send from Gmail.`, link: data.drafts_link }); }
    catch (e) { setMsg({ ok: false, text: formatApiErrorDetail(e?.response?.data?.detail) || "Draft creation failed." }); }
    finally { setBusyDoc(null); }
  };

  return (
    <div className={card} data-testid="drive-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CloudUpload className="h-5 w-5 text-gold-400" />
          <div>
            <h3 className="font-heading text-lg">Google Drive &amp; Gmail</h3>
            <p className="text-white/45 text-xs font-light" data-testid="drive-status">
              {status == null ? "Checking…" : status.connected ? "Connected — exports land in Drive / Managing Disaster Australia; drafts land in your Gmail" : "Not connected — link Google to push docs to Drive and create Gmail drafts"}
            </p>
          </div>
        </div>
        {status?.connected ? (
          <button onClick={disconnect} data-testid="drive-disconnect-btn" className="border border-gold-500/30 text-white/55 hover:text-gold-300 px-4 py-2 text-xs transition-colors">Disconnect</button>
        ) : (
          <button onClick={connect} data-testid="drive-connect-btn" className="bg-gold-500 text-navy-950 font-semibold px-4 py-2 text-sm hover:bg-gold-400 transition-colors">Connect Google Drive</button>
        )}
      </div>
      {status?.connected && (
        <div className="flex items-center gap-2 flex-wrap mt-4">
          <span className="font-mono uppercase tracking-[0.2em] text-[0.5rem] text-white/40">Save to Drive:</span>
          {docs.map(([key, label]) => (
            <button key={key} onClick={() => push(key)} disabled={!!busyDoc} data-testid={`drive-push-${key}`}
              className="inline-flex items-center gap-1.5 border border-gold-500/25 text-white/65 hover:text-gold-300 hover:border-gold-500/50 px-3 py-1.5 text-xs transition-colors disabled:opacity-50">
              {busyDoc === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <CloudUpload className="h-3 w-3" />} {label}
            </button>
          ))}
        </div>
      )}
      {status?.connected && status?.gmail && (
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <span className="font-mono uppercase tracking-[0.2em] text-[0.5rem] text-white/40">Gmail drafts (never auto-sent):</span>
          {[["dwayne", "Dwayne · BRC alpha"], ["devin", "Devin · Clarence"], ["team", "Team email"], ["andrew", "Andrew · NSWRA assessor pitch"], ["andrew_councils", "Andrew · council proposals"], ["dean_invite", "Dean · welcome invitation"]].map(([key, label]) => (
            <button key={key} onClick={() => draft(key)} disabled={!!busyDoc} data-testid={`gmail-draft-${key}`}
              className="inline-flex items-center gap-1.5 border border-emerald-400/30 text-emerald-300/80 hover:text-emerald-300 hover:border-emerald-400/60 px-3 py-1.5 text-xs transition-colors disabled:opacity-50">
              {busyDoc === `draft-${key}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} {label}
            </button>
          ))}
        </div>
      )}
      {status?.connected && status?.gmail === false && (
        <p className="text-amber-300/70 text-xs font-light mt-3" data-testid="gmail-rescope-note">Gmail drafting needs one more permission — disconnect and reconnect Google to approve it.</p>
      )}
      {msg && (
        <div className={`mt-4 text-sm p-3 border flex items-start gap-2 ${msg.ok ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`} data-testid="drive-msg">
          <span className="flex-1">{msg.text}{msg.link && <a href={msg.link} target="_blank" rel="noreferrer" className="underline ml-2 text-xs">Open in Drive</a>}</span>
          <button onClick={() => setMsg(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

function PricingTab() {
  return (
    <div className="space-y-6" data-testid="bh-pricing">
      <div className={card}>
        <Mono className="text-gold-400 text-[0.6rem]">Model</Mono>
        <h2 className="font-heading text-2xl mt-2">Two ways to buy: full suite per-event, or standalone modules annually</h2>
        <p className="text-white/55 font-light mt-3 max-w-3xl">
          The flagship model is per-event, all modules included, no per-user charges. Every council gets the complete feature set for every declared event. Pricing differentiates only by council size
          (annual-revenue tier, self-declared at onboarding). A <span className="text-gold-300">3-event annual cap</span> applies:
          the third event in a financial year auto-upgrades to the discounted Annual Licence with prior per-event payments credited —
          all further events that year are free. Event licences run 24 months from activation (the DRFA claim lifecycle).
          Councils that only need one capability can license <span className="text-gold-300">any module standalone</span> on an annual basis — see the module rate card below.
        </p>
      </div>

      <div className="border border-gold-500/25 bg-navy-900/40 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="border-b border-gold-500/15 bg-navy-950/60">
            {["Tier", "Classification (annual revenue)", "Typical profile", "Per-event (events 1–3)", "Annual licence (post-cap)", "Saving at cap"].map((h) => <th key={h} className={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {TIERS.map((t) => (
              <tr key={t.tier} className="border-b border-white/5">
                <td className={`${td} font-mono text-gold-300`}>{t.tier}</td>
                <td className={td}><span className="text-white">{t.name}</span><div className="text-white/40 text-xs">{t.revenue}</div></td>
                <td className={`${td} text-xs`}>{t.profile}</td>
                <td className={`${td} font-mono text-gold-300`}>{money(t.perEvent)}</td>
                <td className={`${td} font-mono`}>{money(t.annual)}</td>
                <td className={`${td} font-mono text-emerald-300`}>{t.saving}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={card} data-testid="bh-standalone-modules">
        <Mono className="text-gold-400 text-[0.6rem]">Standalone module licensing · annual, per module</Mono>
        <h2 className="font-heading text-2xl mt-2">Start with the module that hurts most — upgrade to the suite with full credit</h2>
        <p className="text-white/55 font-light mt-3 max-w-3xl text-sm">
          Every module runs standalone: same login, same data model, no migration when others are added. Annual licence only
          (per-event pricing is exclusive to the full suite). Each module includes 10 users and standard support.
        </p>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm min-w-[880px]">
            <thead><tr className="border-b border-gold-500/15 bg-navy-950/60">
              {["Module", "What it does", "T1 Small Rural", "T2 Regional", "T3 Large / Urban", "T4 Major"].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {MODULES.map(([name, desc, ...prices]) => (
                <tr key={name} className="border-b border-white/5" data-testid={`bh-module-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <td className={`${td} text-white whitespace-nowrap`}>{name}</td>
                  <td className={`${td} text-xs`}>{desc}</td>
                  {prices.map((p, i) => <td key={i} className={`${td} font-mono text-gold-300`}>{money(p)}</td>)}
                </tr>
              ))}
              <tr className="border-t border-gold-500/25">
                <td className={`${td} text-white/70`} colSpan={2}>All modules standalone (sum)</td>
                {MODULE_TOTALS.map((p, i) => <td key={i} className={`${td} font-mono text-white/50 line-through`}>{money(p)}</td>)}
              </tr>
              <tr>
                <td className={`${td} text-emerald-300`} colSpan={2}>Full-suite annual licence (everything above + all future modules)</td>
                {TIERS.map((t) => <td key={t.tier} className={`${td} font-mono text-emerald-300`}>{money(t.annual)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-4 text-xs text-white/60 font-light">
          <div className="border border-gold-500/15 px-3 py-2.5">· The suite saves <span className="text-emerald-300">~27–28%</span> against the standalone sum at every tier — three or more modules and the suite is always the better buy.</div>
          <div className="border border-gold-500/15 px-3 py-2.5">· <span className="text-white">Upgrade credit:</span> 100% of standalone fees paid in the same financial year are credited against a full-suite upgrade.</div>
          <div className="border border-gold-500/15 px-3 py-2.5">· Popular pairings: EW Prep + FieldClaim Core (evidence-to-form), Contract Admin standalone (works delivery only), Assessor Mode for panel assessors and council reps.</div>
        </div>
        <BundleCalculator />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className={card}>
          <Mono className="text-gold-400 text-[0.6rem]">NSW cost-recovery pathway</Mono>
          <p className="text-white/55 font-light text-sm mt-2 mb-4">
            In NSW the licence fee is conditionally DRFA-reimbursable (EPAR Form 306, "Other Costs — Software Licence").
            Net council cost per event:
          </p>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gold-500/15">{["Tier", "Price", "Net @ 75%", "Net @ 50%"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {TIERS.map((t) => (
                <tr key={t.tier} className="border-b border-white/5">
                  <td className={td}>{t.name}</td>
                  <td className={`${td} font-mono`}>{money(t.perEvent)}</td>
                  <td className={`${td} font-mono text-emerald-300`}>{money(t.net75)}</td>
                  <td className={`${td} font-mono text-gold-300`}>{money(t.net50)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-white/35 text-xs font-light mt-3">Eligibility subject to NSW RA assessment — non-capital, sole eligible-works use, additional cost caused by the event.</p>
        </div>

        <div className={card}>
          <Mono className="text-gold-400 text-[0.6rem]">Cap worked example — Tier 2, 3 events in FY2026-27</Mono>
          <ul className="mt-3 space-y-2 text-sm text-white/70 font-light">
            <li>Event 1 (Aug flooding) — <span className="font-mono text-gold-300">$13,500</span></li>
            <li>Event 2 (Nov storm) — <span className="font-mono text-gold-300">$13,500</span></li>
            <li>Event 3 (Feb cyclone) — auto-upgrade to Annual Licence $28,800, credit $27,000 applied → balance <span className="font-mono text-gold-300">$1,800</span></li>
            <li className="text-emerald-300">Total for the year: $28,800 (vs $40,500 uncapped — saves $11,700). Further events free.</li>
          </ul>
          <div className="mt-5 pt-4 border-t border-gold-500/15">
            <Mono className="text-gold-400 text-[0.6rem]">Consortium discounts (annual licence)</Mono>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {CONSORTIUM.map(([size, disc]) => (
                <div key={size} className="flex justify-between border border-gold-500/15 px-3 py-2 text-xs">
                  <span className="text-white/60">{size}</span><span className="font-mono text-emerald-300">{disc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={card} data-testid="bh-wasa-pricing">
        <Mono className="text-gold-400 text-[0.6rem]">WA &amp; SA pricing pathway</Mono>
        <h2 className="font-heading text-2xl mt-2">Same national framework — different sales math</h2>
        <p className="text-white/55 font-light mt-3 max-w-3xl text-sm">
          The per-event tier table above applies nationally — no separate WA/SA price list. What changes is how the price is
          justified, because neither state offers NSW's licence cost-recovery pathway.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <div className="border border-gold-500/20 p-4">
            <span className="font-heading text-lg text-gold-300">WA — DRFAWA (DFES)</span>
            <ul className="mt-3 space-y-2 text-xs text-white/60 font-light">
              <li>· Licence cost-recovery <span className="text-amber-300">unconfirmed</span> under DRFAWA — do not lead with it. Confirm eligibility with DFES during Phase 1 contact; until then, price is sold on its own merits.</li>
              <li>· Frame as <span className="text-white">consultant substitution + clawback insurance</span>: a rejected or clawed-back claim on a remote shire's road network dwarfs a Tier 1 licence ($8,550).</li>
              <li>· Most WA targets are Tier 1 Small Rural (under $60M revenue) — the cheapest entry point in the table.</li>
              <li>· Align invoicing with the WA claim cycle: activation → claim lodged by 30 Sept after FY end. Invoice deferral to the next financial year (already in terms) fits perfectly.</li>
            </ul>
          </div>
          <div className="border border-gold-500/20 p-4">
            <span className="font-heading text-lg text-gold-300">SA — annual audited claim (DPC)</span>
            <ul className="mt-3 space-y-2 text-xs text-white/60 font-light">
              <li>· SA runs a <span className="text-white">state-controlled regime</span>: DPC discretion, a state-appointed auditor, the highest thresholds and lowest recovery rates in the nation. Councils cannot count on reimbursement — so never pitch cost-recovery.</li>
              <li>· The 24-month event licence spans the whole SA cycle: field capture → FY expenditure → audited annual claim to DPC. One licence, no re-charge at claim season.</li>
              <li>· Sell against the consultant invoice: small councils (Flinders Ranges precedent) pay external consultants more per event than a Tier 1–2 licence just to assemble a lodgeable claim.</li>
              <li>· The structural play is the <span className="text-white">negotiated state-wide LGA SA agreement</span> (consortium table) — in a top-down state, one sector deal beats thirty council pitches.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border border-gold-500/25 bg-navy-900/40 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="border-b border-gold-500/15 bg-navy-950/60">{["Add-on", "Per-event", "Annual", "Description"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>
            {ADDONS.map(([name, pe, an, desc]) => (
              <tr key={name} className="border-b border-white/5">
                <td className={`${td} text-white`}>{name}</td>
                <td className={`${td} font-mono text-gold-300`}>{pe}</td>
                <td className={`${td} font-mono`}>{an}</td>
                <td className={`${td} text-xs`}>{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={card}>
        <Mono className="text-gold-400 text-[0.6rem]">Billing & terms</Mono>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 mt-3 text-sm text-white/60 font-light">
          <span>· Per-event licences invoiced within 5 business days of activation; annual licences at subscription start.</span>
          <span>· Payment terms 30 days — PO, credit card (Stripe) or invoice/EFT.</span>
          <span>· Multi-year (2–3yr) agreements: 10% discount, price-locked for the term.</span>
          <span>· Pricing reviewed each 1 July; current licence holders protected from increases.</span>
          <span>· Direct purchase available now; LGAQ / QAssure / LGNSW / LGPro panel listings in progress.</span>
          <span>· Invoicing can defer to the next financial year by arrangement for budget-constrained councils.</span>
        </div>
      </div>
    </div>
  );
}

function MarketingTab() {
  return (
    <div className="space-y-6" data-testid="bh-marketing">
      <div className={card}>
        <Mono className="text-gold-400 text-[0.6rem]">The central insight</Mono>
        <h2 className="font-heading text-2xl mt-2">A cost-recoverable compliance tool — not a budget line item</h2>
        <p className="text-white/55 font-light mt-3 max-w-3xl">
          In NSW, the licence fee is conditionally eligible for DRFA reimbursement — transforming the sales conversation.
          In QLD and VIC the value proposition shifts to risk reduction (QRA rejects ~15–20% of initial submissions on
          documentation) and time savings (claim compilation cut from 40–80 hours to under 10).
        </p>
        <div className="mt-5 border border-gold-500/30 bg-gold-500/5 p-4 text-gold-200 font-light italic">
          “The only DRFA documentation tool built from the ground up for Australian local government — purpose-built for QRA,
          NSW RA and EMV compliance, priced per event so you only pay when disaster strikes, and potentially cost-recoverable
          through your own DRFA claim.”
        </div>
        <div className="mt-4 border border-sky-400/30 bg-sky-400/5 p-4">
          <Mono className="text-sky-300 text-[0.55rem]">Key differentiator — decision memory</Mono>
          <p className="text-white/60 font-light text-sm mt-2">
            The AI Expert learns from the full NSWRA/NDP library, the council's own documents and an admin-controlled register
            of past NSWRA decisions and outcomes — citing previous determinations as precedent. Institutional claim memory no
            competitor offers: every event makes the next claim stronger. Lead with this in renewals and consortium pitches.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {STATES.map((s) => (
          <div key={s.code} className={card} data-testid={`bh-state-${s.code.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading text-2xl text-gold-300">{s.code}</span>
              <Mono className="text-[0.5rem] text-white/40">{s.lead}</Mono>
            </div>
            <p className="text-white font-light mt-3">{s.message}</p>
            <p className="text-white/50 font-light text-xs mt-2 leading-relaxed">{s.detail}</p>
            <Mono className="text-gold-400 text-[0.55rem] block mt-4 mb-1.5">Key channels</Mono>
            <ul className="space-y-1">
              {s.channels.map((c) => <li key={c} className="text-white/60 text-xs font-light">· {c}</li>)}
            </ul>
            <Mono className="text-gold-400 text-[0.55rem] block mt-4 mb-1.5">Message by persona</Mono>
            <div className="space-y-1.5">
              {s.personas.map(([p, m]) => (
                <div key={p} className="text-xs font-light"><span className="text-gold-300/80">{p}:</span> <span className="text-white/60">{m}</span></div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6" data-testid="bh-wasa-plan">
        {WASA_PLAN.map((s) => (
          <div key={s.code} className={card} data-testid={`bh-wasa-${s.code.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading text-2xl text-gold-300">{s.code} expansion plan</span>
              <Mono className="text-[0.5rem] text-white/40">90-day playbook</Mono>
            </div>
            <div className="mt-3 border border-gold-500/30 bg-gold-500/5 p-3">
              <Mono className="text-gold-400 text-[0.5rem]">The door in</Mono>
              <p className="text-white/70 font-light text-xs mt-1.5 leading-relaxed">{s.door}</p>
            </div>
            <div className="mt-4 space-y-3">
              {s.phases.map(([title, body]) => (
                <div key={title} className="border-l-2 border-gold-500/40 pl-3">
                  <Mono className="text-gold-300/90 text-[0.55rem]">{title}</Mono>
                  <p className="text-white/55 font-light text-xs mt-1 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-gold-500/25 bg-navy-900/40 overflow-x-auto">
          <div className="px-6 pt-5"><Mono className="text-gold-400 text-[0.6rem]">Revenue projections (5% → 15% penetration, ~250 DRFA-active councils)</Mono></div>
          <table className="w-full text-sm mt-2">
            <thead><tr className="border-b border-gold-500/15">{["Year", "Councils (events)", "Avg / council", "Total"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {REVENUE.map(([y, c, avg, tot]) => (
                <tr key={y} className="border-b border-white/5">
                  <td className={td}>{y}</td><td className={`${td} text-xs`}>{c}</td>
                  <td className={`${td} font-mono`}>{avg}</td><td className={`${td} font-mono text-emerald-300`}>{tot}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-white/35 text-xs font-light px-6 pb-5 pt-3">A single 10-council ROC agreement adds ~$288,000 to Year 1 and accelerates Years 2–3.</p>
        </div>
        <div className={card}>
          <Mono className="text-gold-400 text-[0.6rem]">Launch plan & milestones</Mono>
          <div className="mt-3 space-y-2">
            {MILESTONES.map(([m, d]) => (
              <div key={m} className="flex items-center justify-between border-b border-white/5 pb-2 text-sm">
                <span className="text-white/70 font-light">{m}</span>
                <span className="font-mono text-xs text-gold-300">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={card}>
        <Mono className="text-gold-400 text-[0.6rem]">Digital & partnerships</Mono>
        <div className="grid md:grid-cols-3 gap-6 mt-3 text-sm text-white/60 font-light">
          <div>
            <span className="text-white block mb-1.5">Website content</span>
            NSW cost-recovery calculator · state-by-state compliance guide (Tip Sheet 3 / EPAR / GL-1 & GL-3 mapping) · claim-rejection case studies.
          </div>
          <div>
            <span className="text-white block mb-1.5">LinkedIn & email</span>
            Job-title-targeted campaigns ($1,500–3,000/mo per state) · weekly authority posts · event-triggered email within 24h of a new disaster declaration (~2,000-contact database).
          </div>
          <div>
            <span className="text-white block mb-1.5">Partnerships</span>
            QRA / NSW RA / EMV training-material inclusion · LGAQ / LGNSW / MAV preferred-supplier listings · consultant referral partnerships (WSP, GHD, Jacobs, AECOM).
          </div>
        </div>
      </div>
    </div>
  );
}

function StakeholderTab() {
  return (
    <div className="space-y-6" data-testid="bh-stakeholders">
      <div className={card}>
        <Mono className="text-gold-400 text-[0.6rem]">Statement of intent</Mono>
        <h2 className="font-heading text-2xl mt-2">Inclusive design across the full DRFA ecosystem</h2>
        <p className="text-white/55 font-light mt-3 max-w-3xl">
          17 stakeholders mapped across six categories — from the field officer standing in floodwater to the Commonwealth
          auditor reviewing expenditure years later. Prepared to accompany procurement submissions, tender responses and
          partnership proposals as evidence of human-centred design.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {STAKEHOLDER_CATS.map((c) => (
          <div key={c.name} className={card}>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-gold-400" />
              <span className="font-heading text-lg">{c.name}</span>
            </div>
            <Mono className="text-[0.5rem] text-white/40 block mt-1.5">{c.who}</Mono>
            <p className="text-white/55 font-light text-sm mt-3 leading-relaxed">{c.response}</p>
          </div>
        ))}
      </div>

      <div className={card}>
        <Mono className="text-gold-400 text-[0.6rem]">Evidence base & ongoing commitments</Mono>
        <p className="text-white/50 font-light text-sm mt-3">
          Built against QRA Tip Sheet 3 & MARS guides, NSW RA EPAR / submission guidance, VIC GL-1 & GL-3 + VAGO 2025 findings,
          NEMA advisories and the DRFA 2018 Determination — plus LGAQ / LGNSW / MAV member feedback and IPWEAQ consultation.
        </p>
        <ul className="mt-4 space-y-2">
          {COMMITMENTS.map((c) => <li key={c} className="text-white/60 text-sm font-light">· {c}</li>)}
        </ul>
      </div>
    </div>
  );
}
